/**
 * Екзамен-симуляція: день туриста в Польщі.
 * Дві частини — маршрут із рольових сцен і підсумковий тест на слух і переклад.
 */
import { useEffect, useMemo, useState } from 'react'
import { useStore } from '../lib/store.ts'
import { kv } from '../lib/db.ts'
import { shuffle, matchAnswer } from '../lib/text.ts'
import { Bar, Speak, TopBar } from '../components/ui.tsx'
import type { Phrase } from '../lib/course.ts'

/** Порядок станцій — як реально проходить день туриста. */
const ROUTE: { module: string; label: string }[] = [
  { module: 'povitannya', label: 'Прилетів і привітався' },
  { module: 'litak', label: 'Загубився багаж' },
  { module: 'transport', label: 'Таксі до готелю' },
  { module: 'gotel', label: 'Заселення і проблема в номері' },
  { module: 'chysla', label: 'Перша покупка' },
  { module: 'restoran', label: 'Обід із алергією' },
  { module: 'misto', label: 'Заблукав у місті' },
  { module: 'muzei', label: 'Музей' },
  { module: 'apteka', label: 'Аптека' },
  { module: 'potyag', label: 'Потяг до іншого міста' },
  { module: 'ekstreni', label: 'Екстрений випадок' },
]

export function ExamScreen() {
  const { idx } = useStore()
  const [doneRoleplays, setDone] = useState<string[]>([])
  const [tab, setTab] = useState<'route' | 'test'>('route')

  useEffect(() => {
    kv.get<string[]>('roleplaysDone').then((v) => setDone(v ?? [])).catch(() => undefined)
  }, [])

  const stations = ROUTE.map((s) => {
    const m = idx.moduleById.get(s.module)
    const rp = m?.roleplays[0]
    return { ...s, module: m, roleplay: rp, done: rp ? doneRoleplays.includes(rp.id) : false }
  }).filter((s) => s.roleplay)

  const passed = stations.filter((s) => s.done).length

  return (
    <>
      <TopBar title="Екзамен-симуляція" />
      <div className="screen stack">
        <div className="card stack--tight stack">
          <h2>День туриста в Польщі</h2>
          <p className="small muted">
            Пройди всі станції підряд — від прильоту до вечірнього потяга.
            Це фінальна перевірка перед поїздкою.
          </p>
          <Bar value={passed} max={stations.length} good={passed === stations.length} />
          <div className="tiny faint">{passed} з {stations.length} станцій пройдено</div>
        </div>

        <div className="row" style={{ gap: 6 }}>
          <button className={`chip${tab === 'route' ? ' chip--good' : ''}`} style={{ padding: '7px 13px', fontSize: 13 }} onClick={() => setTab('route')}>
            Маршрут дня
          </button>
          <button className={`chip${tab === 'test' ? ' chip--good' : ''}`} style={{ padding: '7px 13px', fontSize: 13 }} onClick={() => setTab('test')}>
            Підсумковий тест
          </button>
        </div>

        {tab === 'route' ? (
          <div className="list">
            {stations.map((s, i) => (
              <a className="item" key={s.roleplay!.id} href={`#/roleplay/${s.module!.id}/${s.roleplay!.id}`}>
                <span className="item__emoji" aria-hidden>{s.done ? '✅' : `${i + 1}`}</span>
                <span className="item__body">
                  <span className="item__title">{s.label}</span>
                  <span className="item__sub">{s.module!.emoji} {s.roleplay!.role}</span>
                </span>
                <span className="item__after">→</span>
              </a>
            ))}
          </div>
        ) : (
          <FinalTest />
        )}
      </div>
    </>
  )
}

// ── Підсумковий тест ──────────────────────────────────────────────────────

const TEST_SIZE = 25

function FinalTest() {
  const { idx, cards, now } = useStore()

  const questions = useMemo(() => {
    // Беремо те, що вже вводилось, віддаючи перевагу хибним друзям і ключовим фразам.
    const seen = idx.order.filter((id) => (cards.get(id)?.state ?? 'new') !== 'new')
    const pool = seen.length >= TEST_SIZE ? seen : idx.order
    const weighted = pool.filter((id) => {
      const p = idx.phraseById.get(id)!
      return (p.tags ?? []).includes('core') || (p.notes ?? []).some((n) => n.kind === 'false-friend')
    })
    const source = weighted.length >= TEST_SIZE ? weighted : pool
    return shuffle(source, Math.floor(now / 86_400_000))
      .slice(0, TEST_SIZE)
      .map((id) => idx.phraseById.get(id)!)
  }, [idx, cards, now])

  const [i, setI] = useState(0)
  const [value, setValue] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)
  const [score, setScore] = useState(0)

  const q: Phrase | undefined = questions[i]

  if (!q) {
    const pct = Math.round((score / questions.length) * 100)
    return (
      <div className="card center stack">
        <div style={{ fontSize: 48 }}>{pct >= 80 ? '🏅' : pct >= 60 ? '👍' : '📚'}</div>
        <h2>{score} з {questions.length}</h2>
        <p className="muted small">
          {pct >= 80 ? 'Готовність до поїздки — впевнена.'
            : pct >= 60 ? 'Основа є, варто ще прогнати слабкі місця.'
            : 'Ще рано. Продовжуй щоденні заняття — планувальник підтягне.'}
        </p>
        <button className="btn btn--primary btn--wide" onClick={() => { setI(0); setScore(0); setChecked(null); setValue('') }}>
          Пройти ще раз
        </button>
      </div>
    )
  }

  const check = () => {
    const r = matchAnswer(value, [q.pl])
    setChecked(r.correct)
    if (r.correct) setScore((s) => s + 1)
  }

  const next = () => { setI((n) => n + 1); setValue(''); setChecked(null) }

  return (
    <div className="card stack">
      <div className="row row--between">
        <span className="tiny faint">Питання {i + 1} з {questions.length}</span>
        <span className="tiny faint">Правильних: {score}</span>
      </div>
      <Bar value={i} max={questions.length} />
      <div style={{ fontSize: 21, fontWeight: 570, marginTop: 8 }}>{q.uk}</div>
      <input
        className="input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && checked === null && value.trim() && check()}
        placeholder="Як це польською?"
        autoCapitalize="off" autoCorrect="off" spellCheck={false}
        disabled={checked !== null}
      />
      {checked !== null && (
        <>
          <div className={`feedback feedback--${checked ? 'ok' : 'no'}`}>
            {checked ? 'Правильно' : `Правильно: ${q.pl}`}
          </div>
          <div className="row" style={{ justifyContent: 'center' }}>
            <Speak audio={q.audio} text={q.pl} auto />
          </div>
        </>
      )}
      {checked === null ? (
        <button className="btn btn--primary btn--wide" disabled={!value.trim()} onClick={check}>Перевірити</button>
      ) : (
        <button className="btn btn--primary btn--wide" onClick={next}>Далі</button>
      )}
    </div>
  )
}
